import { createContext, useContext, useState, useEffect } from "react";
import { useGlobalSearchParams } from "expo-router";

const AcquisitionContext = createContext();

/**
 * Hook to access acquisition tracking context
 * @returns {{
 *  acquisitionRef: string | null,
 *  fbclid: string | null,
 *  utmSource: string | null,
 *  utmMedium: string | null,
 *  utmCampaign: string | null,
 *  utmContent: string | null,
 *  utmAdset: string | null,
 *  utmTerm: string | null,
 *  buildUrlWithParams: (path: string) => string,
 *  clearAcquisitionData: () => void
 * }}
 */
export const useAcquisition = () => {
  const context = useContext(AcquisitionContext);
  if (!context) {
    throw new Error("useAcquisition must be used within AcquisitionProvider");
  }
  return context;
};

/**
 * AcquisitionProvider
 * Captures and maintains the 'ref', 'fbclid', and UTM URL parameters throughout the user session
 *
 * This enables tracking of marketing campaign sources (e.g., Instagram ads, Facebook ads)
 * without using localStorage or cookies.
 *
 * Convention for ref values:
 * - Instagram ads: ref=ig_ad_launch_video1, ref=ig_ad_es_coaches_carousel3
 * - Instagram profile: ref=ig_profile_bio_main, ref=ig_profile_linktree_trial
 *
 * The fbclid parameter is automatically added by Facebook to track ad clicks.
 *
 * All parameters are:
 * - Captured once when user first enters the app
 * - Maintained in memory during the session
 * - Propagated through all navigation within the funnel
 * - Saved to Firestore when coach completes onboarding
 * - Lost if user closes/reloads without the parameters
 */
export const AcquisitionProvider = ({ children }) => {
  const [acquisitionRef, setAcquisitionRef] = useState(null);
  const [fbclid, setFbclid] = useState(null);
  const [utmSource, setUtmSource] = useState(null);
  const [utmMedium, setUtmMedium] = useState(null);
  const [utmCampaign, setUtmCampaign] = useState(null);
  const [utmContent, setUtmContent] = useState(null);
  const [utmAdset, setUtmAdset] = useState(null);
  const [utmTerm, setUtmTerm] = useState(null);
  const params = useGlobalSearchParams();

  useEffect(() => {
    // Capture the 'ref' parameter from URL if present
    // Note: Other URL parameters (like 'lang') are handled separately by their own hooks
    // and will not interfere with acquisition tracking
    const refParam = params.ref;

    if (refParam && !acquisitionRef) {
      console.log("[AcquisitionContext] Captured ref parameter:", refParam);
      setAcquisitionRef(refParam);
    }
  }, [params.ref, acquisitionRef]);

  useEffect(() => {
    // Capture the 'fbclid' parameter from URL if present
    // This is Facebook's click identifier automatically added to URLs from Facebook ads
    const fbclidParam = params.fbclid;

    if (fbclidParam && !fbclid) {
      console.log(
        "[AcquisitionContext] Captured fbclid parameter:",
        fbclidParam
      );
      setFbclid(fbclidParam);
    }
  }, [params.fbclid, fbclid]);

  useEffect(() => {
    // Capture UTM parameters from URL if present
    const utmSourceParam = params.utm_source;
    const utmMediumParam = params.utm_medium;
    const utmCampaignParam = params.utm_campaign;
    const utmContentParam = params.utm_content;
    const utmAdsetParam = params.utm_adset;
    const utmTermParam = params.utm_term;

    if (utmSourceParam && !utmSource) {
      console.log(
        "[AcquisitionContext] Captured utm_source parameter:",
        utmSourceParam
      );
      setUtmSource(utmSourceParam);
    }

    if (utmMediumParam && !utmMedium) {
      console.log(
        "[AcquisitionContext] Captured utm_medium parameter:",
        utmMediumParam
      );
      setUtmMedium(utmMediumParam);
    }

    if (utmCampaignParam && !utmCampaign) {
      console.log(
        "[AcquisitionContext] Captured utm_campaign parameter:",
        utmCampaignParam
      );
      setUtmCampaign(utmCampaignParam);
    }

    if (utmContentParam && !utmContent) {
      console.log(
        "[AcquisitionContext] Captured utm_content parameter:",
        utmContentParam
      );
      setUtmContent(utmContentParam);
    }

    if (utmAdsetParam && !utmAdset) {
      console.log(
        "[AcquisitionContext] Captured utm_adset parameter:",
        utmAdsetParam
      );
      setUtmAdset(utmAdsetParam);
    }

    if (utmTermParam && !utmTerm) {
      console.log(
        "[AcquisitionContext] Captured utm_term parameter:",
        utmTermParam
      );
      setUtmTerm(utmTermParam);
    }
  }, [
    params.utm_source,
    params.utm_medium,
    params.utm_campaign,
    params.utm_content,
    params.utm_adset,
    params.utm_term,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
  ]);

  /**
   * Helper function to build URLs with acquisition parameters
   * @param {string} path - The path to navigate to (e.g., '/signup', '/login')
   * @returns {string} The path with ref and/or fbclid parameters appended if they exist
   */
  const buildUrlWithParams = (path) => {
    if (
      !acquisitionRef &&
      !fbclid &&
      !utmSource &&
      !utmMedium &&
      !utmCampaign &&
      !utmContent &&
      !utmAdset &&
      !utmTerm
    ) {
      return path;
    }

    const params = [];
    if (acquisitionRef) params.push(`ref=${acquisitionRef}`);
    if (fbclid) params.push(`fbclid=${fbclid}`);
    if (utmSource) params.push(`utm_source=${utmSource}`);
    if (utmMedium) params.push(`utm_medium=${utmMedium}`);
    if (utmCampaign) params.push(`utm_campaign=${utmCampaign}`);
    if (utmContent) params.push(`utm_content=${utmContent}`);
    if (utmAdset) params.push(`utm_adset=${utmAdset}`);
    if (utmTerm) params.push(`utm_term=${utmTerm}`);

    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}${params.join("&")}`;
  };

  /**
   * Clear the acquisition data (e.g., on logout)
   */
  const clearAcquisitionData = () => {
    console.log("[AcquisitionContext] Clearing acquisition data");
    setAcquisitionRef(null);
    setFbclid(null);
    setUtmSource(null);
    setUtmMedium(null);
    setUtmCampaign(null);
    setUtmContent(null);
    setUtmAdset(null);
    setUtmTerm(null);
  };

  const value = {
    acquisitionRef,
    fbclid,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
    buildUrlWithParams,
    clearAcquisitionData,
  };

  return (
    <AcquisitionContext.Provider value={value}>
      {children}
    </AcquisitionContext.Provider>
  );
};
